// Design source: React Flow's `freehand-draw-pro-example`.
// Points are stored in node-local coordinates; path is recomputed via
// perfect-freehand on every render to support resize and restyle.
import { memo, useMemo, useCallback } from 'react';
import { type NodeProps, type Node } from '@xyflow/react';
import { pointsToPath } from "@/features/canvas/engine/primitives/geometry/path";
import styles from './component.module.css';
import type { FreedrawNodeData } from './types';
import { makeDefault } from './defaults';
import { withDefaults } from '../with-defaults';
import { SelectionTransformer } from '@/features/canvas/engine/selection/selection-transformer';
import { useNodeActions } from "@/features/canvas/engine/wiring/contexts/node-actions-context";
import { useCanvasDoc } from "@/features/canvas/engine/wiring/contexts/canvas-doc-context";
import { resolveNodeAriaLabel } from '../../a11y/aria-labels';
import { useParentLabelResolver } from '../../a11y/parent-label-context';

type FreedrawNodeShape = Node<FreedrawNodeData, 'freedraw'>;

/**
 * Immutable SVG renderer for freehand strokes. Applies point scaling when the
 * node is resized (width/initialSize ratio). No editing UI — strokes are
 * written by the DrawingOverlay in the playground (Task 14) or canvas tools (S9+).
 */
export default memo(function FreedrawNode({
  id,
  data,
  width,
  height,
  selected,
  dragging,
  parentId,
}: NodeProps<FreedrawNodeShape>) {
  // Merge canonical defaults so legacy strokes / fixtures missing
  // strokeColor/strokeWidth fall back rather than rendering as undefined.
  const d = withDefaults<FreedrawNodeData>(data, makeDefault as () => FreedrawNodeData);
  const renderWidth = width ?? d.initialSize.width;
  const renderHeight = height ?? d.initialSize.height;
  const scaleX = renderWidth / d.initialSize.width;
  const scaleY = renderHeight / d.initialSize.height;
  const parentLabel = useParentLabelResolver();
  const ariaLabel = resolveNodeAriaLabel(
    { type: 'freedraw', data: {}, parentId: parentId ?? null },
    parentLabel,
  );

  const scaledPoints = useMemo(
    () => d.points.map(([x, y, p]) => [x * scaleX, y * scaleY, p] as [number, number, number]),
    [d.points, scaleX, scaleY],
  );
  const strokeWidth = d.strokeWidth;
  const actions = useNodeActions();
  const doc = useCanvasDoc();

  const setStrokeColor = useCallback((color: string) => {
    actions?.onUpdateStroke(id, color, d.strokeWidth);
  }, [actions, id, d.strokeWidth]);

  const setStrokeWidth = useCallback((width: number) => {
    actions?.onUpdateStroke(id, d.strokeColor, width);
  }, [actions, id, d.strokeColor]);

  const freedrawState = useMemo(() => ({
    strokeColor: d.strokeColor,
    strokeWidth: d.strokeWidth,
    setStrokeColor,
    setStrokeWidth,
  }), [d.strokeColor, d.strokeWidth, setStrokeColor, setStrokeWidth]);

  const opacity = d.strokeOpacity ?? 1;

  const strokeSvg = (
    <svg
      viewBox={`0 0 ${renderWidth} ${renderHeight}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
    >
      <path d={pointsToPath(scaledPoints, strokeWidth)} fill={d.strokeColor} opacity={opacity} />
    </svg>
  );

  return (
    <SelectionTransformer
      nodeId={id}
      nodeType="freedraw"
      selected={!!selected && !dragging}
      dragging={!!dragging}
      doc={doc ?? undefined}
      onExpand={actions?.onExpand}
      ghostContent={strokeSvg}
      freedrawState={freedrawState}
    >
      {/* React Flow node container — see engine/README §Raw-HTML exception */}
      <div
        className={styles.freedraw}
        data-testid="freedraw-node"
        data-node-id={id}
        aria-label={ariaLabel}
      >
        <svg
          className={styles.freedrawSvg}
          data-selected={selected ? 'true' : 'false'}
          width={renderWidth}
          height={renderHeight}
        >
          <path
            className={styles.freedrawPath}
            d={pointsToPath(scaledPoints, strokeWidth)}
            fill={d.strokeColor}
            opacity={opacity}
          />
        </svg>
      </div>
    </SelectionTransformer>
  );
});
